import _pairs from 'lodash/toPairs';
import type { WCardVariationColorSummaryView } from '@zola/svc-web-api-ts-client';
import type { ActiveFilters } from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/FilterTypes';
import type { SeoColor } from 'cards/constants/seoColors';
import { getActiveThumbnailUrl } from 'paper/utils/helpers';
import generateUuid from 'lib/uuid';
import { Ad, CardListing, CardListingOptions, Digital } from './types';

/**
 * Checks the specified card's theme data and to find an image matching the currently selected color and shape filters.
 *
 * @returns The thumbnail's full URL (if found) OR an empty string
 */
export const getThumbnailImage = (
  card: CardListing,
  searchFilters: ActiveFilters,
  paperImageTileRedesignVariation: string
): string => {
  const {
    motif_color_recommendations: motifColorRecommendations,
    color_recommendations: colorRecommendations,
    colors,
  } = card;
  const { motifs, seo_colors, multiColor } = searchFilters;
  let activeColor: WCardVariationColorSummaryView | undefined;

  // First, check if there are color recommendations for the currently selected MOTIF search filters (e.g. 'FALL')
  if (motifs?.length && motifColorRecommendations) {
    const motifFilter = motifs.find(filter => motifColorRecommendations[filter]);
    if (motifFilter) {
      activeColor = colors.find(color => color.value === motifColorRecommendations[motifFilter]);
    }
  }

  // Otherwise, check if there are color recommendations (e.g. 'ffffff') for the current SEO_COLOR search filters (e.g. 'WHITE')
  if (!activeColor && seo_colors?.length && colorRecommendations) {
    const colorFilter = seo_colors.find(filter => colorRecommendations[filter as SeoColor]);
    if (colorFilter) {
      activeColor = colors.find(
        color => color.value === colorRecommendations[colorFilter as SeoColor]
      );
    }
  }

  // Otherwise, check if there is a color match for any of a multi- coloredcard's palette
  if (!activeColor && multiColor?.length) {
    activeColor = colors.find(
      color =>
        color.nrf_color_code_label && multiColor.includes(color.nrf_color_code_label.toUpperCase())
    );
  }

  // Otherwise, just choose the first color
  if (!activeColor) {
    activeColor = colors[0];
  }

  // Make sure the URL always starts with "https:" IF DEFINED. Otherwise, return an empty string.
  const imageUrl =
    getActiveThumbnailUrl(activeColor, searchFilters, paperImageTileRedesignVariation) || '';
  return imageUrl && imageUrl.indexOf('http') < 0 ? `https:${imageUrl}` : imageUrl;
};

const insertAdOnInterval = (
  initialAdIndex: number,
  insertAdEvery: number,
  columnsPerRow: number,
  list: CardListingOptions[]
) => {
  let result = list;
  let rotationIndex = 1;
  for (let i = initialAdIndex + insertAdEvery; i < result.length; i += insertAdEvery) {
    let positionToInsertAd = i + rotationIndex;
    if (positionToInsertAd >= result.length) positionToInsertAd = i;
    result = [
      ...result.slice(0, positionToInsertAd),
      <Ad>{ type: 'Ad', uuid: generateUuid() },
      ...result.slice(positionToInsertAd),
    ];
    rotationIndex += 1;
    if (rotationIndex === columnsPerRow) rotationIndex = 0;
  }
  return result;
};

export const injectPlpRedesignAdTiles = (
  cards: CardListingOptions[],
  isMobile = false
): CardListingOptions[] => {
  let result: CardListingOptions[] = [];

  const columns = isMobile ? 3 : 5;
  const INITIAL_AD_INDEX = columns * 2 - 1;
  const INSERT_AD_EVERY = 24;

  if (cards.length < INITIAL_AD_INDEX) return [...cards];

  const indexAdjustment = isMobile ? 0 : 1;
  result = [
    ...cards.slice(0, INITIAL_AD_INDEX - indexAdjustment),
    <Ad>{ type: 'Ad', uuid: generateUuid() },
    ...cards.slice(INITIAL_AD_INDEX - indexAdjustment),
  ];

  result = insertAdOnInterval(INITIAL_AD_INDEX, INSERT_AD_EVERY, columns, result);
  return result;
};

export const injectDigitalTiles = (cards: CardListingOptions[]): CardListingOptions[] => {
  let result = [...cards];
  const COLUMNS_PER_ROW = 4;
  const INITIAL_AD_INDEX = COLUMNS_PER_ROW * 4 - 2;
  if (cards.length < INITIAL_AD_INDEX) return result;
  const INSERT_AD_EVERY = COLUMNS_PER_ROW * 6;
  let rotationIndex = 1;
  const loopStart = INITIAL_AD_INDEX;
  for (let i = loopStart; i < 48; i += INSERT_AD_EVERY) {
    let positionToInsertAd = i - rotationIndex - 2;
    if (positionToInsertAd >= result.length) positionToInsertAd = i;
    result = [
      ...result.slice(0, positionToInsertAd),
      <Digital>{ type: 'Digital', uuid: generateUuid() },
      ...result.slice(positionToInsertAd),
    ];
    rotationIndex += 1;
    if (rotationIndex === COLUMNS_PER_ROW) rotationIndex = 0;
  }

  return result;
};

// Tracking utils

/*
 * Consolidate filters with multiple options
 * e.g. custom photo or orientation, that are considered
 * different filters per option (...)
 */
export const consolidateFiltersByKey = (filters: Record<string, any>) =>
  Object.keys(filters).reduce((consolidatedFilters, filter) => {
    const currentFilterValue = filters[filter];
    let filterPair = {};
    if (
      (filter === 'has_custom_photo' || filter === 'has_no_custom_photo') &&
      !!currentFilterValue
    ) {
      const photoOptionValues = consolidatedFilters.photo_option
        ? [...consolidatedFilters.photo_option, filter]
        : [filter];
      filterPair = { photo_option: photoOptionValues };
    } else if ((filter === 'is_landscape' || filter === 'is_portrait') && !!currentFilterValue) {
      const orientationValues = consolidatedFilters.orientation
        ? [...consolidatedFilters.orientation, filter]
        : [filter];
      filterPair = { orientation: orientationValues };
    } else {
      filterPair = { [filter]: currentFilterValue };
    }
    return { ...consolidatedFilters, ...filterPair };
  }, {} as Record<string, any>);

/*
 * Mapping to match tracking contracts enums
 */
const TRACKING_FILTERS = {
  has_foil: 'FOIL',
  is_letterpress: 'LETTERPRESS',
  motifs: 'MOTIFS',
  seo_colors: 'COLORS',
  silhouettes: 'SHAPE',
  photo_option: 'PHOTO_OPTION',
  has_magnet: 'MAGNET',
  is_vow_renewal: 'RECEPTION_ONLY',
  featured_artist: 'FEATURED_ARTISTS',
  orientation: 'ORIENTATION',
};

/*
 * Transform all strings in uppercase to match tracking contract enums
 */
const upperCaseKeysValues = (list: [string, any][]) =>
  list.map(([key, value]) => {
    let val = value;
    if (Array.isArray(val)) {
      val = val.map(el => el.toUpperCase());
    }
    return { type: key.toUpperCase(), value: val };
  });

const pairedFilters = (filters: Record<string, any>): [string, any][] => _pairs(filters);

/*
 * Filter only the pair key:value we want to track
 * as per contract.
 * Relies on the mapping defined above to respect
 * contract enums as keys.
 */
export const trackedFilters = (filters: Record<string, any>) =>
  upperCaseKeysValues(
    pairedFilters(filters).reduce((filteredFilters, filter) => {
      const [key, value] = filter;
      if (
        TRACKING_FILTERS[key as keyof typeof TRACKING_FILTERS] &&
        (value === true || value?.length > 0)
      ) {
        const filteredFilter: [string, any] = [
          TRACKING_FILTERS[key as keyof typeof TRACKING_FILTERS],
          value,
        ];
        filteredFilters.push(filteredFilter);
      }
      return filteredFilters;
    }, [] as [string, any][])
  );
